/**
 * NetworkClub Service
 * Service voor het ophalen en beheren van netwerkclubs
 */

import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '../firebase';
import { NetworkClub, NetworkClubSearchFilters, DUTCH_PROVINCES, ClubModerationStatus, NetworkClubMember, NetworkClubGuest } from '../types/networkclub';

// Demo data voor als er nog geen clubs in Firebase zijn
const DEMO_CLUBS: NetworkClub[] = [
  {
    id: 'demo-1',
    name: 'Amsterdam Entrepreneurs Network',
    description: 'Het grootste netwerk voor ondernemers in de regio Amsterdam. Wekelijkse meetups, kennissessies en networking events.',
    region: 'Noord-Holland',
    city: 'Amsterdam',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 245,
    foundedYear: 2018,
    categories: ['Ondernemers', 'Startups', 'Tech & IT'],
    meetingFrequency: 'Wekelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-2',
    name: 'Rotterdam Business Club',
    description: 'Connecting ambitious professionals in Rotterdam. Monthly dinners and quarterly conferences.',
    region: 'Zuid-Holland',
    city: 'Rotterdam',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 180,
    foundedYear: 2015,
    categories: ['Ondernemers', 'Finance & Legal', 'Consultancy'],
    meetingFrequency: 'Maandelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-3',
    name: 'Utrecht Startup Hub',
    description: 'Community voor startups en scale-ups in de regio Utrecht. Pitch nights, mentor sessions en co-working.',
    region: 'Utrecht',
    city: 'Utrecht',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 156,
    foundedYear: 2020,
    categories: ['Startups', 'Scale-ups', 'Tech & IT'],
    meetingFrequency: 'Tweewekelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-4',
    name: 'Eindhoven Tech Network',
    description: 'Het technetwerk van Brainport Eindhoven. Voor founders, developers en tech professionals.',
    region: 'Noord-Brabant',
    city: 'Eindhoven',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 312,
    foundedYear: 2017,
    categories: ['Tech & IT', 'Startups', 'Scale-ups'],
    meetingFrequency: 'Maandelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-5',
    name: 'Groningen Ondernemers Vereniging',
    description: 'Lokale ondernemersvereniging voor het MKB in Groningen en omstreken.',
    region: 'Groningen',
    city: 'Groningen',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 89,
    foundedYear: 2012,
    categories: ['Ondernemers', 'ZZP & Freelance', 'Retail & Horeca'],
    meetingFrequency: 'Maandelijks',
    isVerified: false,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-6',
    name: 'Women in Business NL',
    description: 'Landelijk netwerk voor vrouwelijke ondernemers. Inspiratie, support en zakelijke connecties.',
    region: 'Noord-Holland',
    city: 'Amsterdam',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 420,
    foundedYear: 2016,
    categories: ['Vrouwelijke ondernemers', 'Ondernemers', 'Consultancy'],
    meetingFrequency: 'Maandelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-7',
    name: 'Den Haag International Business',
    description: 'Netwerk voor internationale ondernemers en expats in Den Haag.',
    region: 'Zuid-Holland',
    city: 'Den Haag',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 134,
    foundedYear: 2019,
    categories: ['Ondernemers', 'Import & Export', 'Consultancy'],
    meetingFrequency: 'Maandelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-8',
    name: 'Limburg Business Network',
    description: 'Het zakelijke netwerk voor Zuid-Limburg. Van Maastricht tot Heerlen.',
    region: 'Limburg',
    city: 'Maastricht',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 98,
    foundedYear: 2014,
    categories: ['Ondernemers', 'ZZP & Freelance'],
    meetingFrequency: 'Tweewekelijks',
    isVerified: false,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-9',
    name: 'Friesland Ondernemers Collectief',
    description: 'Fryske ûndernimmers feriening. Netwerken in het Noorden.',
    region: 'Friesland',
    city: 'Leeuwarden',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 67,
    foundedYear: 2021,
    categories: ['Ondernemers', 'Duurzaamheid'],
    meetingFrequency: 'Maandelijks',
    isVerified: false,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  {
    id: 'demo-10',
    name: 'Creative Industries Amsterdam',
    description: 'Netwerk voor creatieven: designers, marketeers, content creators en meer.',
    region: 'Noord-Holland',
    city: 'Amsterdam',
    country: 'Nederland',
    website: 'https://example.com',
    memberCount: 203,
    foundedYear: 2018,
    categories: ['Creatieve sector', 'Marketing & Communicatie', 'ZZP & Freelance'],
    meetingFrequency: 'Tweewekelijks',
    isVerified: true,
    moderationStatus: 'approved',
    logoUrl: ''
  },
  // Demo pending clubs voor admin testing
  {
    id: 'demo-pending-1',
    name: 'Nieuwe Club Aanvraag',
    description: 'Dit is een test aanvraag voor een nieuwe netwerkclub.',
    region: 'Noord-Holland',
    city: 'Haarlem',
    country: 'Nederland',
    kvkNumber: '12345678',
    categories: ['Ondernemers'],
    meetingFrequency: 'Maandelijks',
    isVerified: false,
    moderationStatus: 'pending',
    organizerName: 'Test Gebruiker',
    organizerEmail: 'test@example.com',
    logoUrl: ''
  },
  {
    id: 'demo-pending-2',
    name: 'Brabant Startup Circle',
    description: 'Nieuwe startup community voor de regio Brabant.',
    region: 'Noord-Brabant',
    city: 'Tilburg',
    country: 'Nederland',
    kvkNumber: '87654321',
    categories: ['Startups', 'Tech & IT'],
    meetingFrequency: 'Tweewekelijks',
    isVerified: false,
    moderationStatus: 'pending',
    organizerName: 'Jan de Vries',
    organizerEmail: 'jan@example.com',
    logoUrl: ''
  }
];

class NetworkClubService {
  private collectionName = 'networkClubs';

  /**
   * Haal alle netwerkclubs op met optionele filters (alleen approved voor normale users)
   */
  async getNetworkClubs(filters?: NetworkClubSearchFilters): Promise<NetworkClub[]> {
    try {
      // Probeer eerst uit Firebase te halen
      const clubsRef = collection(db, this.collectionName);
      let q = query(
        clubsRef, 
        where('moderationStatus', '==', 'approved'),
        orderBy('name')
      );

      // Filter op regio als opgegeven
      if (filters?.region) {
        q = query(
          clubsRef, 
          where('moderationStatus', '==', 'approved'),
          where('region', '==', filters.region), 
          orderBy('name')
        );
      }

      const snapshot = await getDocs(q);
      
      let clubs: NetworkClub[] = [];
      
      if (snapshot.empty) {
        // Gebruik demo data als er geen clubs in Firebase zijn (alleen approved)
        console.log('📍 Geen clubs in Firebase, gebruik demo data');
        clubs = DEMO_CLUBS.filter(c => c.moderationStatus === 'approved');
      } else {
        clubs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as NetworkClub));
      }

      // ✅ NIEUW: Filter alleen actieve clubs (5+ leden)
      clubs = clubs.filter(club => club.isActive === true);

      // Client-side filtering
      if (filters) {
        if (filters.searchTerm) {
          const term = filters.searchTerm.toLowerCase();
          clubs = clubs.filter(club => 
            club.name.toLowerCase().includes(term) ||
            club.description?.toLowerCase().includes(term) ||
            club.city?.toLowerCase().includes(term)
          );
        }

        if (filters.region && snapshot.empty) {
          // Als we demo data gebruiken, filter client-side
          clubs = clubs.filter(club => club.region === filters.region);
        }

        if (filters.city) {
          clubs = clubs.filter(club => 
            club.city?.toLowerCase() === filters.city?.toLowerCase()
          );
        }

        if (filters.category) {
          clubs = clubs.filter(club => 
            club.categories?.includes(filters.category!)
          );
        }
      }

      return clubs;
    } catch (error) {
      console.error('Error fetching network clubs:', error);
      // Fallback naar demo data bij error (alleen approved)
      return this.filterDemoClubs(filters);
    }
  }

  /**
   * Filter demo clubs (fallback) - alleen approved
   */
  private filterDemoClubs(filters?: NetworkClubSearchFilters): NetworkClub[] {
    // Filter eerst op approved status
    let clubs = DEMO_CLUBS.filter(c => c.moderationStatus === 'approved');

    if (filters) {
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        clubs = clubs.filter(club => 
          club.name.toLowerCase().includes(term) ||
          club.description?.toLowerCase().includes(term) ||
          club.city?.toLowerCase().includes(term)
        );
      }

      if (filters.region) {
        clubs = clubs.filter(club => club.region === filters.region);
      }

      if (filters.city) {
        clubs = clubs.filter(club => 
          club.city?.toLowerCase() === filters.city?.toLowerCase()
        );
      }

      if (filters.category) {
        clubs = clubs.filter(club => 
          club.categories?.includes(filters.category!)
        );
      }
    }

    return clubs;
  }

  /**
   * Haal een specifieke club op
   */
  async getNetworkClub(clubId: string): Promise<NetworkClub | null> {
    try {
      // Check eerst demo data
      const demoClub = DEMO_CLUBS.find(c => c.id === clubId);
      if (demoClub) return demoClub;

      const docRef = doc(db, this.collectionName, clubId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as NetworkClub;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching network club:', error);
      return null;
    }
  }

  /**
   * Haal unieke provincies op met club counts
   */
  async getRegionsWithCounts(): Promise<{ region: string; count: number }[]> {
    try {
      const clubs = await this.getNetworkClubs();
      const regionCounts = new Map<string, number>();

      clubs.forEach(club => {
        const current = regionCounts.get(club.region) || 0;
        regionCounts.set(club.region, current + 1);
      });

      // Voeg alle provincies toe (ook met 0 clubs)
      DUTCH_PROVINCES.forEach(province => {
        if (!regionCounts.has(province)) {
          regionCounts.set(province, 0);
        }
      });

      return Array.from(regionCounts.entries())
        .map(([region, count]) => ({ region, count }))
        .sort((a, b) => b.count - a.count);
    } catch (error) {
      console.error('Error fetching regions:', error);
      return [];
    }
  }

  /**
   * Voeg een nieuwe club toe (voor admins/organizers)
   */
  async addNetworkClub(club: Omit<NetworkClub, 'id'>): Promise<string | null> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...club,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding network club:', error);
      return null;
    }
  }

  /**
   * Update een bestaande club
   */
  async updateNetworkClub(clubId: string, updates: Partial<NetworkClub>): Promise<boolean> {
    try {
      const docRef = doc(db, this.collectionName, clubId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating network club:', error);
      return false;
    }
  }

  /**
   * ✅ NIEUW: Haal club op via entityId
   */
  async getNetworkClubByEntityId(entityId: string): Promise<NetworkClub | null> {
    try {
      const clubsRef = collection(db, this.collectionName);
      const q = query(clubsRef, where('entityId', '==', entityId), firestoreLimit(1));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) return null;
      
      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      } as NetworkClub;
    } catch (error) {
      console.error('Error fetching club by entityId:', error);
      return null;
    }
  }

  /**
   * ✅ NIEUW: Haal member count op voor club
   */
  async getMemberCount(clubId: string): Promise<number> {
    try {
      const clubRef = doc(db, this.collectionName, clubId);
      const clubSnap = await getDoc(clubRef);
      if (!clubSnap.exists()) return 0;
      
      const club = clubSnap.data();
      if (!club.entityId) return 0;
      
      // Tel entityMembers
      const { collection: getCollection, query: getQuery, where: getWhere, getDocs: getDocsQuery } = await import('firebase/firestore');
      const membersQuery = getQuery(
        getCollection(db, 'entityMembers'),
        getWhere('entityId', '==', club.entityId)
      );
      const membersSnap = await getDocsQuery(membersQuery);
      
      return membersSnap.size;
    } catch (error) {
      console.error('Error getting member count:', error);
      return 0;
    }
  }

  /**
   * ✅ NIEUW: Update active & visible status op basis van member count
   */
  async updateClubVisibility(clubId: string): Promise<{
    isActive: boolean;
    isVisible: boolean;
    memberCount: number;
  }> {
    try {
      const clubRef = doc(db, this.collectionName, clubId);
      const clubSnap = await getDoc(clubRef);
      
      if (!clubSnap.exists()) {
        throw new Error('Club not found');
      }
      
      const club = clubSnap.data() as NetworkClub;
      const memberCount = await this.getMemberCount(clubId);
      const minMembers = club.minMembersRequired || 5;
      
      // ✅ ACTIEF pas bij 5 leden
      const isActive = memberCount >= minMembers;
      // ✅ ZICHTBAAR pas bij 5 leden (ook in zoekfunctie/carrousel)
      const isVisible = memberCount >= minMembers;
      
      // Update club
      await updateDoc(clubRef, {
        memberCount,
        isActive,
        isVisible,
        updatedAt: serverTimestamp()
      });
      
      return { isActive, isVisible, memberCount };
    } catch (error) {
      console.error('Error updating club visibility:', error);
      throw error;
    }
  }

  // =============================================================================
  // ADMIN FUNCTIONS
  // =============================================================================

  /**
   * Haal alle clubs op voor admin (inclusief pending en rejected)
   */
  async getAllClubsForAdmin(): Promise<NetworkClub[]> {
    try {
      const clubsRef = collection(db, this.collectionName);
      const q = query(clubsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Return all demo clubs including pending
        console.log('📍 Admin view: Gebruik alle demo data inclusief pending');
        return [...DEMO_CLUBS];
      }

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NetworkClub));
    } catch (error) {
      console.error('Error fetching all clubs for admin:', error);
      return [...DEMO_CLUBS];
    }
  }

  /**
   * Haal alleen pending clubs op voor admin review
   */
  async getPendingClubs(): Promise<NetworkClub[]> {
    try {
      const clubsRef = collection(db, this.collectionName);
      const q = query(
        clubsRef, 
        where('moderationStatus', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Return demo pending clubs
        return DEMO_CLUBS.filter(c => c.moderationStatus === 'pending');
      }

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as NetworkClub));
    } catch (error) {
      console.error('Error fetching pending clubs:', error);
      return DEMO_CLUBS.filter(c => c.moderationStatus === 'pending');
    }
  }

  /**
   * Keur een club goed
   */
  async approveClub(clubId: string, adminUserId: string): Promise<boolean> {
    try {
      // Check if it's a demo club
      if (clubId.startsWith('demo-')) {
        console.log('📍 Demo club approved (not persisted):', clubId);
        return true;
      }

      const docRef = doc(db, this.collectionName, clubId);
      await updateDoc(docRef, {
        moderationStatus: 'approved',
        moderatedAt: serverTimestamp(),
        moderatedBy: adminUserId,
        isVerified: true,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error approving club:', error);
      return false;
    }
  }

  /**
   * Wijs een club af
   */
  async rejectClub(clubId: string, adminUserId: string, reason?: string): Promise<boolean> {
    try {
      // Check if it's a demo club
      if (clubId.startsWith('demo-')) {
        console.log('📍 Demo club rejected (not persisted):', clubId);
        return true;
      }

      const docRef = doc(db, this.collectionName, clubId);
      await updateDoc(docRef, {
        moderationStatus: 'rejected',
        moderationNote: reason || 'Afgewezen door admin',
        moderatedAt: serverTimestamp(),
        moderatedBy: adminUserId,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error rejecting club:', error);
      return false;
    }
  }

  /**
   * Verwijder een club
   */
  async deleteClub(clubId: string): Promise<boolean> {
    try {
      // Check if it's a demo club
      if (clubId.startsWith('demo-')) {
        console.log('📍 Demo club delete (not persisted):', clubId);
        return true;
      }

      const docRef = doc(db, this.collectionName, clubId);
      await updateDoc(docRef, { 
        moderationStatus: 'rejected',
        updatedAt: serverTimestamp() 
      });
      return true;
    } catch (error) {
      console.error('Error deleting club:', error);
      return false;
    }
  }

  /**
   * Haal club statistieken op voor admin dashboard
   */
  async getClubStats(): Promise<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  }> {
    try {
      const clubs = await this.getAllClubsForAdmin();
      return {
        total: clubs.length,
        pending: clubs.filter(c => c.moderationStatus === 'pending').length,
        approved: clubs.filter(c => c.moderationStatus === 'approved').length,
        rejected: clubs.filter(c => c.moderationStatus === 'rejected').length
      };
    } catch (error) {
      console.error('Error getting club stats:', error);
      return { total: 0, pending: 0, approved: 0, rejected: 0 };
    }
  }

  /**
   * ✅ NIEUW: Nodig gast uit
   */
  async inviteGuest(clubId: string, guestData: Omit<NetworkClubGuest, 'id' | 'invitedAt'>): Promise<string | null> {
    try {
      const guestRef = await addDoc(collection(db, 'networkClubGuests'), {
        ...guestData,
        invitedAt: serverTimestamp()
      });
      return guestRef.id;
    } catch (error) {
      console.error('Error inviting guest:', error);
      return null;
    }
  }

  /**
   * ✅ NIEUW: Haal gasten op
   */
  async getClubGuests(clubId: string, eventId?: string): Promise<NetworkClubGuest[]> {
    try {
      let guestsQuery = query(
        collection(db, 'networkClubGuests'),
        where('clubId', '==', clubId)
      );
      
      if (eventId) {
        guestsQuery = query(
          collection(db, 'networkClubGuests'),
          where('clubId', '==', clubId),
          where('eventId', '==', eventId)
        );
      }
      
      const snapshot = await getDocs(guestsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        invitedAt: doc.data().invitedAt?.toDate() || new Date()
      } as NetworkClubGuest));
    } catch (error) {
      console.error('Error getting club guests:', error);
      return [];
    }
  }

  /**
   * ✅ NIEUW: Haal leden op (uit entityMembers)
   */
  async getClubMembers(clubId: string): Promise<NetworkClubMember[]> {
    try {
      // Haal eerst club op om entityId te krijgen
      const club = await this.getNetworkClub(clubId);
      if (!club || !club.entityId) return [];

      const membersQuery = query(
        collection(db, 'entityMembers'),
        where('entityId', '==', club.entityId)
      );
      const membersSnap = await getDocs(membersQuery);
      
      const members: NetworkClubMember[] = [];
      for (const memberDoc of membersSnap.docs) {
        const memberData = memberDoc.data();
        // Haal user data op
        const userDoc = await getDoc(doc(db, 'users', memberData.userId));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        members.push({
          id: memberData.userId,
          clubId: clubId,
          entityId: club.entityId,
          name: userData.displayName || userData.name || 'Onbekend',
          email: userData.email || '',
          photoURL: userData.photoURL,
          role: memberData.role || 'member',
          joinedAt: memberData.createdAt?.toDate() || new Date(),
          status: 'active'
        });
      }
      
      return members;
    } catch (error) {
      console.error('Error getting club members:', error);
      return [];
    }
  }
}

export const networkClubService = new NetworkClubService();
export default networkClubService;
